import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "./components/ui/sonner";

// Layouts
import PublicLayout from "./layouts/PublicLayout";
import DashboardLayout from "./layouts/DashboardLayout";

// Public Pages (lazy loaded)
const LandingPage = lazy(() => import("./pages/LandingPage"));
const BrowsePage = lazy(() => import("./pages/BrowsePage"));
const ItemDetailPage = lazy(() => import("./pages/ItemDetailPage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const VerifyOtpPage = lazy(() => import("./pages/VerifyOtpPage"));

// Dashboard Pages (lazy loaded)
const DashboardHome = lazy(() => import("./pages/dashboard/DashboardHome"));
const MyListings = lazy(() => import("./pages/dashboard/MyListings"));
const MyRentals = lazy(() => import("./pages/dashboard/MyRentals"));
const AddItem = lazy(() => import("./pages/dashboard/AddItem"));
const EditItem = lazy(() => import("./pages/dashboard/EditItem"));
const Messages = lazy(() => import("./pages/dashboard/Messages"));
const Profile = lazy(() => import("./pages/dashboard/Profile"));
const SellerRequestsPage = lazy(() => import("./pages/dashboard/SellerRequestsPage"));

import "@/App.css";

const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/auth" replace />;
    }
    
    return children;
};

const PageLoader = () => (
    <div className="min-h-screen flex items-center justify-center bg-background" aria-live="polite">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
    </div>
);

function AppRoutes() {
    return (
        <Suspense fallback={<PageLoader />}>
            <Routes>
                {/* Public Routes */}
                <Route element={<PublicLayout />}>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/browse" element={<BrowsePage />} />
                    <Route path="/item/:id" element={<ItemDetailPage />} />

                </Route>
                
                {/* Auth Routes */}
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/verify-otp" element={<VerifyOtpPage />} />
                <Route path="/chat/:itemId" element={
                    <ProtectedRoute>
                        <ChatPage />
                    </ProtectedRoute>
                } />
                
                {/* Dashboard Routes */}
                <Route path="/dashboard" element={
                    <ProtectedRoute>
                        <DashboardLayout />
                    </ProtectedRoute>
                }>
                    <Route index element={<DashboardHome />} />
                    <Route path="listings" element={<MyListings />} />
                    <Route path="rentals" element={<MyRentals />} />
                    <Route path="add-item" element={<AddItem />} />
                    <Route path="edit-item/:id" element={<EditItem />} />
                    <Route path="messages" element={<Messages />} />
                    <Route path="messages/:userId" element={<Messages />} />
                    <Route path="requests" element={<SellerRequestsPage />} />
                    <Route path="profile" element={<Profile />} />
                </Route>
            </Routes>
        </Suspense>
    );
}

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <BrowserRouter>
                    <AppRoutes />
                    <Toaster position="top-right" richColors />
                </BrowserRouter>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
